import { DayPilot, DayPilotMonth } from "@daypilot/daypilot-lite-react";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CalendarToolbar } from "./components/CalendarToolbar";
import { CalendarFilters } from "./components/CalendarFilters";
import { NewReportModal } from "./components/NewReportModal";
import { useCalendarNavigation } from "./hooks/useCalendarNavigation";
import { useCalendarData } from "./hooks/useCalendarData";
import { useCalendarFilters } from "./hooks/useCalendarFilters";
import { transformReportsToEvents } from "./utils/eventTransformer";
import { toDayPilotDate } from "./utils/calendarHelpers";
import { calendarConfig } from "./config/calendarConfig";
import { successToast } from "../../helpers/successToast";
import { PlannedReport } from "../../api/reportsApi";
import { getPlannedVisitPeople } from "../../utils/plannedVisitUtils";
import { LoadingRegion, Skeleton } from "../../components/ui/Skeleton";

export const CalendarPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [calendarRef, setCalendarRef] = useState<any>(null);
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [showEditPlannedModal, setShowEditPlannedModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPlannedReport, setSelectedPlannedReport] = useState<PlannedReport | null>(null);
  
  const {
    currentMonth,
    handlePreviousMonth,
    handleNextMonth,
    handleToday,
  } = useCalendarNavigation();

  const {
    selectedStudy,
    selectedEmail,
    handleStudyChange,
    handleEmailChange,
  } = useCalendarFilters();

  const { reports, plannedReports, isLoading, error } = useCalendarData(currentMonth);

  // Filter reports based on selected filters
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (selectedStudy && report.studyName !== selectedStudy) return false;
      if (selectedEmail && report.createdBy !== selectedEmail) return false;
      return true;
    });
  }, [reports, selectedStudy, selectedEmail]);

  // Filter planned reports based on selected filters
  const filteredPlannedReports = useMemo(() => {
    return plannedReports.filter(planned => {
      if (selectedStudy && planned.studyName !== selectedStudy) return false;
      if (selectedEmail && !getPlannedVisitPeople(planned).includes(selectedEmail)) return false;
      return true;
    });
  }, [plannedReports, selectedStudy, selectedEmail]);

  // Update calendar display when currentMonth changes
  useEffect(() => {
    if (calendarRef && calendarRef.control && !calendarRef.control.disposed) {
      try {
        calendarRef.control.startDate = new DayPilot.Date(toDayPilotDate(currentMonth));
        calendarRef.control.update();
      } catch (error) {
        console.warn('Failed to update calendar:', error);
      }
    }
  }, [currentMonth, calendarRef]);

  const events = useMemo(
    () => transformReportsToEvents(filteredReports, filteredPlannedReports, t),
    [filteredReports, filteredPlannedReports, t]
  );

  // Handlers for new report modal
  const handleTimeRangeSelected = (args: any) => {

    const selectedDate = new Date(args.start.value);
    const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    setSelectedDate(date);
    setShowNewReportModal(true);
  };

  const handleReportCreated = () => {
    successToast();
  };

  const handleAddReport = () => {
    setSelectedDate(new Date());
    setShowNewReportModal(true);
  };

  // Event click handler - needs to be outside useState to access current state
  const handleEventClick = (args: any) => {
    const eventId = args.e.data.id;
    const isPlannedEvent = events.find(e => e.id === eventId)?.isPlanned;
    
    if (isPlannedEvent) {
      // Navigate to visit page
      navigate(`/visit/${eventId}`);
    } else {
      // Open regular report in new tab
      window.open(`/reports/${eventId}`, '_blank');
    }
  };

  // Pierwsze wczytanie: układ strony z siatką miesiąca (przy zmianie miesiąca zostają poprzednie dane)
  if (isLoading) {
    return (
      <LoadingRegion label={t("loadingCalendar")} className="p-6">
        <div className="mb-4">
          <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">{t("reportsCalendar")}</h1>
          <p className="mt-1 text-sm text-[var(--sc-muted)]">{t("viewAllReportsByVisitDates")}</p>
        </div>
        <div className="mb-4 p-4 bg-[var(--sc-bg)] rounded-[10px] border border-[var(--sc-border)] flex gap-4">
          <Skeleton className="h-9 w-56 rounded-lg" />
          <Skeleton className="h-9 w-56 rounded-lg" />
        </div>
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-28 rounded-full ml-auto" />
        </div>
        <div className="bg-[var(--sc-surface)] rounded-[10px] border border-[var(--sc-border)] overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[var(--sc-border)]">
            {["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"].map((d) => (
              <div key={d} className="px-2 py-2 text-[11px] font-medium text-[var(--sc-muted)] text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 35 }, (_, i) => (
              <div key={i} className="h-[96px] border-r border-b border-[var(--sc-border)] p-2 flex flex-col gap-1.5 [&:nth-child(7n)]:border-r-0">
                <Skeleton className="h-2.5 w-4 self-end" />
                {i % 3 === 0 && <Skeleton className="h-4 w-full rounded-[4px]" />}
                {i % 5 === 1 && <Skeleton className="h-4 w-3/4 rounded-[4px]" />}
              </div>
            ))}
          </div>
        </div>
      </LoadingRegion>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">
          {t("errorLoadingCalendar")}: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Title */}
      <div className="mb-4">
        <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">{t("reportsCalendar")}</h1>
        <p className="mt-1 text-sm text-[var(--sc-muted)]">
          {t("viewAllReportsByVisitDates")}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 p-4 bg-[var(--sc-bg)] rounded-[10px] border border-[var(--sc-border)]">
        <CalendarFilters
          reports={reports}
          plannedReports={plannedReports}
          selectedStudy={selectedStudy}
          selectedEmail={selectedEmail}
          onStudyChange={handleStudyChange}
          onEmailChange={handleEmailChange}
        />
      </div>

      {/* Sticky Navigation and Status Bar */}
      <CalendarToolbar
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        onAddReport={handleAddReport}
        plannedReportsCount={filteredPlannedReports.length}
        currentMonth={currentMonth}
      />

      {/* Calendar */}
      <div className="bg-[var(--sc-surface)] rounded-[10px] border border-[var(--sc-border)] calendar-container">
        <DayPilotMonth 
          {...calendarConfig}
          events={events} 
          startDate={new DayPilot.Date(toDayPilotDate(currentMonth))}
          key={`${currentMonth.getFullYear()}-${currentMonth.getMonth()}`}
          ref={setCalendarRef}
          onEventClick={handleEventClick}
          onTimeRangeSelected={handleTimeRangeSelected}
        />
      </div>

      {/* New Report Modal */}
      <NewReportModal
        isOpen={showNewReportModal}
        onClose={() => setShowNewReportModal(false)}
        selectedDate={selectedDate}
        onReportCreated={handleReportCreated}
      />

      {/* Edit Planned Report Modal */}
      {/* <EditPlannedReportModal
        isOpen={showEditPlannedModal}
        onClose={() => setShowEditPlannedModal(false)}
        plannedReport={selectedPlannedReport}
        onReportUpdated={handleReportCreated}
      /> */}
    </div>
  );
};